import React, { useState } from 'react';
import { AlertTriangle, Trash2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import DeleteProjectModal from '../modals/DeleteProjectModal';

interface Workspace {
  id: string;
  name: string;
  directory_path: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  deleted?: boolean;
  deleted_at?: string;
  scheduled_hard_delete_at?: string;
}

interface DeleteProjectSectionProps {
  workspace: Workspace | null;
  workspaceId: string;
  api: any;
}

const DeleteProjectSection: React.FC<DeleteProjectSectionProps> = ({ workspace, workspaceId, api }) => {
  const navigate = useNavigate();
  const { closeProjectSettings } = useSettingsModal();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);


  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setError(null);
  };

  const handleDeleteConfirm = async (projectId: string) => {
    setIsDeleting(true);
    setError(null);

    try {
      await api.delete(`/workspaces/${projectId}`);

      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 500));
      // Close the project settings modal
      closeProjectSettings();
      
      // Redirect to applications page after successful deletion
      navigate('/repositories', { replace: true });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete project';
      setError(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setError(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Danger Zone
        </h2>
        <p className="text-sm text-ui-text-secondary">
          Actions in this section are irreversible. Please proceed with caution.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="animate-in slide-in-from-top-2 fade-in duration-300 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/10 dark:to-orange-900/10 border border-red-200 dark:border-red-800/50 rounded-xl p-6 space-y-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-ui-text-primary mb-2">
              Delete this project
            </h3>
            <p className="text-sm text-ui-text-secondary mb-1">
              Once you delete a project, it will be marked for deletion and permanently removed after 30 days.
            </p>
            <div className="flex items-center gap-2 text-xs text-ui-text-tertiary mt-2">
              <Clock className="w-3 h-3" />
              <span>You can restore it within the 30-day grace period</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleDeleteClick}
          className="px-4 py-2.5 rounded-lg text-white font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          disabled={!workspace}
          aria-label="Delete project"
          data-tour="delete-project-button"
        >
          Delete project
        </button>
      </div>

      {/* Delete Project Modal */}
      <DeleteProjectModal
        isOpen={showDeleteModal}
        project={workspace}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default DeleteProjectSection;
